import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, ImagePlus, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { api, downloadBlob, fileUrl, formatApiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import type { Listing } from "@/types";
import { Sticker } from "@/components/Doodles";
import { StatusPill } from "@/components/StatusPill";
import { EtsyPanel } from "@/components/EtsyPanel";
import { ListingForm } from "@/components/ListingForm";
import { CustomDesignPanel } from "@/components/CustomDesignPanel";
import { CatalogPanel } from "@/components/CatalogPanel";

export default function ListingPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const meta = useMeta();
  const [l, setL] = useState<Listing | null>(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/listings/${id}`).then((r) => setL(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (l?.status !== "building") { return; }
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [l?.status, load]);

  const run = async (key: string, fn: () => Promise<any>, ok: string) => {
    setBusy(key);
    try {
      const { data } = await fn();
      if (data?.id) setL(data);
      toast.success(ok);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!l || !meta) return <p className="text-sm text-[#23324A]/60">Loading…</p>;
  const building = l.status === "building";

  const remove = async () => {
    if (!window.confirm(`Delete "${l.name}"?`)) return;
    await api.delete(`/listings/${l.id}`);
    nav("/admin/listings");
  };

  return (
    <div data-testid="listing-page">
      <Link to="/admin/listings" className="mb-6 inline-flex items-center gap-2 text-sm font-bold text-[#23324A]" data-testid="back-to-listings"><ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> All listings</Link>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-black text-[#23324A] sm:text-4xl" data-testid="listing-name">{l.name}</h1>
        <StatusPill status={l.status} testId="listing-status" />
        {l.categories.map((c, i) => <Sticker key={c} label={c} tilt={i % 2 ? 3 : -3} />)}
      </div>
      {l.review_note && <p className="mb-6 rounded-2xl border-2 border-[#F08A6C] bg-[#FDE6DF] p-4 text-sm font-semibold text-[#23324A]" data-testid="review-note">Sent back: {l.review_note}</p>}
      {l.error && <p className="mb-6 rounded-2xl border-2 border-[#F08A6C] bg-white p-4 text-sm font-semibold text-[#C2412D]" data-testid="listing-error">{l.error}</p>}

      <div className="grid gap-10 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <section className="min-w-0 space-y-6">
          <div className="paper-card overflow-hidden">
            <div className="aspect-[5/4] bg-[#F3EEE3]">
              {l.thumbnail_path ? <img src={fileUrl(l.thumbnail_web_path || l.thumbnail_path)} alt="Etsy thumbnail" className="h-full w-full object-cover" data-testid="listing-thumbnail" />
                : <div className="flex h-full items-center justify-center text-sm font-semibold text-[#23324A]/50">{building ? "Building…" : "No thumbnail"}</div>}
            </div>
            <div className="flex flex-wrap gap-2 p-4">
              <button className="btn-outline" disabled={!!busy || building} data-testid="ai-mockup-btn"
                onClick={() => run("mockup", () => api.post(`/listings/${l.id}/mockup`), "AI mockup photo added to the thumbnail")}>
                <ImagePlus className="h-4 w-4" strokeWidth={2.5} /> {busy === "mockup" ? "Creating photo… (~30s)" : l.mockup_path ? "New AI mockup photo" : "AI mockup photo"}
              </button>
              {l.mockup_path && (
                <button className="btn-outline" disabled={!!busy} data-testid="remove-mockup-btn"
                  onClick={() => run("unmock", () => api.delete(`/listings/${l.id}/mockup`), "Back to the flat-lay thumbnail")}>Use flat-lay instead</button>
              )}
              <button className="btn-outline" disabled={!!busy || building} data-testid="rebuild-btn"
                onClick={() => run("rebuild", () => api.post(`/listings/${l.id}/rebuild`), "Rebuilding files, thumbnail and copy")}>
                <RefreshCw className="h-4 w-4" strokeWidth={2.5} /> Rebuild all
              </button>
            </div>
          </div>

          <div className="paper-card p-5">
            <h2 className="mb-3 font-display text-lg font-extrabold text-[#23324A]">Files ({l.files.length})</h2>
            <div className="flex flex-wrap gap-2">
              {l.files.map((f) => (
                <button key={f.paper} className="btn-outline" data-testid={`download-${f.paper}-btn`}
                  onClick={() => downloadBlob(`/files/${f.path}?download=${encodeURIComponent(f.name)}`, f.name).catch((e) => toast.error(formatApiError(e)))}>
                  <Download className="h-4 w-4" strokeWidth={2.5} /> {meta.papers[f.paper]} PDF
                </button>
              ))}
            </div>
            <ul className="mt-4 space-y-1 text-sm text-[#23324A]/80" data-testid="listing-contents">
              {l.contents.map((c, i) => <li key={i}>• {c}</li>)}
            </ul>
          </div>

          <CustomDesignPanel listing={l} onChange={setL} disabled={building} />

          {l.preview_paths.length > 0 && (
            <div>
              <h2 className="mb-3 font-display text-lg font-extrabold text-[#23324A]">Pages</h2>
              <div className="flex gap-4 overflow-x-auto pb-3" data-testid="listing-previews">
                {l.preview_paths.map((p, i) => <img key={p} src={fileUrl(p)} alt={`Page ${i + 1}`} className="h-64 shrink-0 rounded-lg border-2 border-[#23324A] bg-white" loading="lazy" />)}
              </div>
            </div>
          )}
        </section>

        <section className="min-w-0 space-y-6">
          <ListingForm listing={l} badges={meta.badges} onSaved={setL} disabled={building} />
          <CatalogPanel listing={l} onSaved={setL} />
          <div className="flex flex-wrap gap-3">
            <button className="btn-outline" disabled={!!busy || building} data-testid="regenerate-copy-btn"
              onClick={() => run("copy", () => api.post(`/listings/${l.id}/copy`), "New Etsy copy written")}>
              <Sparkles className="h-4 w-4" strokeWidth={2.5} /> {busy === "copy" ? "Writing…" : "Rewrite copy with AI"}
            </button>
            {l.status === "draft" && (
              <button className="btn-coral" disabled={!!busy} data-testid="submit-review-btn"
                onClick={() => run("submit", () => api.post(`/listings/${l.id}/review`, { action: "submit" }), "Added to the review queue")}>Send to review</button>
            )}
            <button className="btn-ghost" onClick={remove} data-testid="delete-listing-btn"><Trash2 className="h-4 w-4" strokeWidth={2.5} /> Delete</button>
          </div>
          <EtsyPanel listing={l} demo={meta.etsy_demo} onChange={setL} />
        </section>
      </div>
    </div>
  );
}
